import { createSignal, onMount, onCleanup, createMemo, createEffect, getOwner, runWithOwner } from "solid-js";
import { persistantStore } from "../../../../../store/persistantStore";
import { sourcesStore } from "../../../../../store/sourcesStore";
import {
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  initializeDateRange,
  setSelectedSources as filterStoreSetSelectedSources,
} from "../../../../../store/filterStore";
import { user } from "../../../../../store/userStore";
import { apiEndpoints } from "@config/env";
import { getData, setupMediaContainerScaling } from "../../../../../utils/global";
import { logPageLoad } from "../../../../../utils/logging";
import { debug as logDebug } from "../../../../../utils/console";
import FleetSourcesGate from "../../../../../components/utilities/FleetSourcesGate";
import SummaryTablesSettings from "../../../../../components/menus/SummaryTablesSettings";
import DailyTrainingSettingsSection from "../../../helpers/DailyTrainingSettingsSection";
import { ensureFleetSourcesContextReady } from "../../../../../utils/fleetSourcesReady";
import { initializeSourceSelections } from "../../../../../utils/sourceInitialization";
import { persistentSettingsService } from "../../../../../services/persistentSettingsService";
import { getProjectSummaryTablesFilters } from "../../../../../services/projectFiltersService";
import {
  buildSummaryTablesHistoryFilters,
  type SummaryTablesHistoryFilters,
} from "../../../helpers/dailyTrainingSettingsApi";

const { selectedClassName, selectedProjectId, selectedYear, selectedEvent } = persistantStore;
const PAGE_TITLE = "FLEET SUMMARY";
const PAGE_SUBTITLE =
  "The intention of this page is to summarize setting differences between teams in similar conditions, with adjustable filtering criteria to provide flexibility in querying data and making various comparisons, including racing vs training, etc.";

export default function FleetSummaryTablesPage() {
  const [gradeOptions, setGradeOptions] = createSignal<number[]>([-1, 1, 2]);
  const [stateOptions, setStateOptions] = createSignal<string[]>(["H0", "H1", "H2"]);
  const [selectedGrades, setSelectedGrades] = createSignal<number[]>([1]);
  const [selectedStates, setSelectedStates] = createSignal<string[]>(["H0"]);
  const [trainingRacing, setTrainingRacing] = createSignal<"TRAINING" | "RACING" | null>(null);

  const [filterYear, setFilterYear] = createSignal("");
  const [filterEvent, setFilterEvent] = createSignal("");
  const [filterConfigValue, setFilterConfigValue] = createSignal("");
  const [filterState, setFilterState] = createSignal("");

  const [selectedSourceIds, setSelectedSourceIds] = createSignal<Set<number>>(new Set());
  const [showTimeline, setShowTimeline] = createSignal(true);

  const historyFilters = createMemo((): SummaryTablesHistoryFilters =>
    buildSummaryTablesHistoryFilters({
      selectedGrades: selectedGrades(),
      selectedStates: selectedStates(),
      filterYear: filterYear(),
      filterEvent: filterEvent(),
      filterConfig: filterConfigValue(),
      trainingRacing: trainingRacing(),
    })
  );

  const resolvedSourceNames = createMemo((): string[] => {
    const sources = sourcesStore.sources();
    const ids = selectedSourceIds();
    if (ids.size === 0) {
      return sources.map((s) => String(s.source_name)).filter((n) => n.trim() !== "");
    }
    return sources
      .filter((s) => ids.has(Number(s.source_id)))
      .map((s) => String(s.source_name))
      .filter((n) => n.trim() !== "");
  });

  const dataSourcesOptions = createMemo(() =>
    sourcesStore.sources().map((source) => ({
      key: `source-${source.source_id}`,
      label: source.source_name,
      signal: [
        () => selectedSourceIds().has(Number(source.source_id)),
        (value: boolean) => {
          const current = selectedSourceIds();
          const next = new Set(current);
          if (value) { next.add(Number(source.source_id)); }
          else { next.delete(Number(source.source_id)); }
          setSelectedSourceIds(next);
        },
      ] as [() => boolean, (value: boolean) => void],
    }))
  );

  const toggleGradeFilter = (grade: number) => {
    const current = selectedGrades();
    const next = current.includes(grade) ? [] : [grade];
    setSelectedGrades(next);
  };

  const toggleStateFilter = (state: string) => {
    const current = selectedStates();
    const next = current.includes(state) ? current.filter((s) => s !== state) : [...current, state];
    setSelectedStates(next);
  };

  const mapSourceNamesToIds = (names: string[]): Set<number> => {
    const normalized = new Set(names.map((n) => String(n).toLowerCase().trim()).filter(Boolean));
    const ids = new Set<number>();
    for (const source of sourcesStore.sources()) {
      const name = String(source.source_name ?? "").toLowerCase().trim();
      const sourceId = Number(source.source_id);
      if (name && normalized.has(name) && Number.isFinite(sourceId)) {
        ids.add(sourceId);
      }
    }
    return ids;
  };

  const initializeSourcesFromPersistentSettings = async (): Promise<void> => {
    if (selectedSourceIds().size > 0) {
      return;
    }

    const sources = sourcesStore.sources();
    if (!sourcesStore.isReady() || sources.length === 0) {
      return;
    }

    try {
      const sourceNames = await initializeSourceSelections();
      if (sourceNames.length > 0) {
        const ids = mapSourceNamesToIds(sourceNames);
        if (ids.size > 0) {
          setSelectedSourceIds(ids);
          filterStoreSetSelectedSources(sourceNames);
          logDebug("FleetSummaryTables: Initialized sources from persistent settings", sourceNames);
          return;
        }
      }

      const allIds = new Set(
        sources.map((s) => Number(s.source_id)).filter((id) => Number.isFinite(id))
      );
      if (allIds.size > 0) {
        setSelectedSourceIds(allIds);
        filterStoreSetSelectedSources(
          sources.map((s) => String(s.source_name)).filter((n) => n.trim() !== "")
        );
        logDebug("FleetSummaryTables: No matching persisted sources; defaulting to all sources");
      }
    } catch (err) {
      logDebug("FleetSummaryTables: initializeSourcesFromPersistentSettings failed", err);
      if (selectedSourceIds().size === 0) {
        const allIds = new Set(
          sources.map((s) => Number(s.source_id)).filter((id) => Number.isFinite(id))
        );
        setSelectedSourceIds(allIds);
      }
    }
  };

  const initializeDefaultDateRange = async (): Promise<void> => {
    try {
      const className = selectedClassName();
      const projectId = selectedProjectId();
      if (!className || !projectId) { return; }

      const result = await getData(
        `${apiEndpoints.app.datasets}/last_date?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&source_id=0`
      );
      if (!result.success || !result.data) { return; }

      const endDateValue = new Date(String(result.data).trim());
      if (Number.isNaN(endDateValue.getTime())) { return; }

      const startDateValue = new Date(endDateValue.getTime());
      startDateValue.setDate(startDateValue.getDate() - 365);

      const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      setEndDate(formatDate(endDateValue));
      setStartDate(formatDate(startDateValue));
    } catch (err) {
      logDebug("FleetSummaryTables: initializeDefaultDateRange failed", err);
    }
  };

  const loadFiltersFromPersistentSettings = async (): Promise<void> => {
    const currentUser = user();
    if (!currentUser?.user_id) { return; }
    try {
      const className = selectedClassName();
      const projectId = selectedProjectId();
      if (!className || !projectId) { return; }

      const settings = await persistentSettingsService.loadSettings(
        currentUser.user_id,
        className,
        projectId
      );

      if (settings?.summaryTablesFilters) {
        const f = settings.summaryTablesFilters;
        if (f.grades && f.grades.length > 0) { setSelectedGrades(f.grades); }
        if (f.states && f.states.length > 0) { setSelectedStates(f.states); }
        if (f.trainingRacing === "TRAINING" || f.trainingRacing === "RACING") {
          setTrainingRacing(f.trainingRacing);
        }
        if (typeof f.showTimeline === "boolean") { setShowTimeline(f.showTimeline); }
      } else if (settings?.performanceHistoryDateRange) {
        const dr = settings.performanceHistoryDateRange;
        if (dr.startDate) { setStartDate(dr.startDate); }
        if (dr.endDate) { setEndDate(dr.endDate); }
      }
    } catch (err) {
      logDebug("FleetSummaryTables: loadFiltersFromPersistentSettings failed", err);
    }
  };

  onMount(() => {
    logDebug("FleetSummaryTables: mount");
    logPageLoad("project/all/reports/FleetSummaryTables.tsx", PAGE_TITLE, "Loaded");
    const owner = getOwner();

    void (async () => {
      const className = selectedClassName();
      const projectId = selectedProjectId();
      await ensureFleetSourcesContextReady({ className, projectId });
      await initializeSourcesFromPersistentSettings();
      await initializeDateRange();

      const projectFilters =
        className && projectId
          ? await getProjectSummaryTablesFilters(className, Number(projectId), "1970-01-01")
          : null;

      if (projectFilters == null) {
        await loadFiltersFromPersistentSettings();
      }

      if (projectFilters != null) {
        if (projectFilters.grades != null) { setSelectedGrades(projectFilters.grades); }
        if (projectFilters.states != null) { setSelectedStates(projectFilters.states); }
        if (projectFilters.training_racing != null) { setTrainingRacing(projectFilters.training_racing); }
        if (projectFilters.start_date) { setStartDate(projectFilters.start_date); }
        if (projectFilters.end_date) { setEndDate(projectFilters.end_date); }
      }

      const dsYear = selectedYear();
      const dsEvent = selectedEvent();
      if (
        (!filterYear() || filterYear().trim() === "") &&
        dsYear != null &&
        String(dsYear).trim() !== "" &&
        String(dsYear).trim().toUpperCase() !== "ALL"
      ) {
        setFilterYear(String(dsYear).trim());
      }
      if (
        (!filterEvent() || filterEvent().trim() === "") &&
        dsEvent != null &&
        String(dsEvent).trim() !== "" &&
        String(dsEvent).trim().toUpperCase() !== "ALL"
      ) {
        setFilterEvent(String(dsEvent).trim());
      }

      if (!startDate() || !endDate()) {
        await initializeDefaultDateRange();
      }

      runWithOwner(owner, () => {
        const cleanupScaling = setupMediaContainerScaling({
          logPrefix: "FleetSummaryTables",
          scaleToWidth: true,
        });
        onCleanup(() => cleanupScaling());
      });
    })();
  });

  createEffect(() => {
    const ids = selectedSourceIds();
    const sources = sourcesStore.sources();
    if (ids.size === 0 || sources.length === 0) {
      return;
    }
    const names: string[] = [];
    sources.forEach((source) => {
      const sourceId = Number(source.source_id);
      if (ids.has(sourceId) && source.source_name) {
        names.push(String(source.source_name));
      }
    });
    if (names.length > 0) {
      filterStoreSetSelectedSources(names);
    }
  });

  const persistAppliedSettings = async (): Promise<void> => {
    const currentUser = user();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!currentUser?.user_id || !className || !projectId) { return; }
    try {
      await persistentSettingsService.saveSettings(currentUser.user_id, className, Number(projectId), {
        summaryTablesFilters: {
          grades: selectedGrades(),
          states: selectedStates(),
          trainingRacing: trainingRacing(),
          showTimeline: showTimeline(),
        },
        performanceHistoryDateRange: {
          startDate: startDate() || undefined,
          endDate: endDate() || undefined,
        },
      });
    } catch (err) {
      logDebug("FleetSummaryTables: persistAppliedSettings failed", err);
    }
  };

  return (
    <div id="media-container" class="cheat-sheet-page training-summary-page">
      <FleetSourcesGate message="Loading fleet sources...">
        <div class="container relative">
          <div class="performance-charts-scroll-container">
            <div class="performance-charts-scroll-inner cheat-sheet-scroll-inner">
              <header class="training-summary-header">
                <h1 class="report-page-title cheat-sheet-title">{PAGE_TITLE}</h1>
                <p class="training-summary-subtitle">{PAGE_SUBTITLE}</p>
              </header>
              <DailyTrainingSettingsSection
                mode="fleet"
                pageLabel={PAGE_TITLE}
                titleLevel="none"
                fleetContext={{
                  startDate: () => startDate() || "",
                  endDate: () => endDate() || "",
                  filters: () => historyFilters(),
                  sourceNames: () => resolvedSourceNames(),
                  showTimeline: () => showTimeline(),
                }}
                settingsControl={
                  <div class="training-summary-settings-control">
                    <SummaryTablesSettings
                      useIconTrigger={true}
                      dataSourcesOptions={dataSourcesOptions()}
                      gradeOptions={gradeOptions()}
                      stateOptions={stateOptions()}
                      selectedGrades={() => selectedGrades()}
                      selectedStates={() => selectedStates()}
                      selectedTrainingRacing={trainingRacing()}
                      onTrainingRacingFilterChange={setTrainingRacing}
                      setGradeOptions={setGradeOptions}
                      setStateOptions={setStateOptions}
                      toggleGradeFilter={toggleGradeFilter}
                      toggleStateFilter={toggleStateFilter}
                      filterYear={filterYear}
                      filterEvent={filterEvent}
                      filterConfig={filterConfigValue}
                      filterState={filterState}
                      onFilterYearChange={setFilterYear}
                      onFilterEventChange={setFilterEvent}
                      onFilterConfigChange={setFilterConfigValue}
                      onFilterStateChange={setFilterState}
                      onApplyFilters={() => void persistAppliedSettings()}
                      showTimeline={showTimeline}
                      onTimelineChange={setShowTimeline}
                    />
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </FleetSourcesGate>
    </div>
  );
}
